import React, { JSX, useEffect } from "react";
import {
    FormRadioCardList,
    RadioCard,
    RadioCardStyle,
} from "components/form/form-radio-card-list/form-radio-card-list";
import { Icons } from "components/icons/constants/icons";
import { IconSizes } from "components/icons/constants/icon-sizes";
import { Paragraph, ParagraphSize } from "components/typography/paragraph/paragraph";
import { FormCalendarDateRangePicker } from "components/form/form-calendar-date-range-picker/form-calendar-date-range-picker";
import { ReportsBaseProps } from "components/reports/reports-base-props";
import { DataTable } from "components/tables/data-table/data-table";
import { ReportSelection } from "components/reports/report-selection/report-selection";
import {
    REPORT_TYPES,
    REPORT_TYPES_DESCRIPTIONS,
} from "components/reports/report-all-trainings/report-all-trainings-constants";
import {
    initialState,
    initializer,
    reducer,
    validate,
} from "components/reports/report-all-trainings/report-all-trainings-reducer";
import { useReportReducer } from "utilities/hooks/use-report-reducer";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { t } from "utilities/localization/t";
import { StringUtils } from "utilities/string-utils";
import ReportOrganizationSelection from "components/reports/components/report-organization-selection/report-organization-selection";
import ReportInstructorSelection from "components/reports/components/report-instructor-selection/report-instructor-selection";
import ReportInvoiceStatusSelection from "components/reports/components/report-invoice-status-selection/report-invoice-status-selection";
import { RoleType } from "models/enumerations/users/role-type";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import "./report-all-trainings.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ReportAllTrainingsProps extends ReportsBaseProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "report-all-trainings";

type ReportKey = keyof typeof REPORT_TYPES_DESCRIPTIONS;

const REPORT_TYPES_CARDS = Object.values(REPORT_TYPES).map(
    (key): RadioCard => ({
        icon: Icons.Reporting,
        text: REPORT_TYPES_DESCRIPTIONS[key],
        value: key,
    })
);


const AEN_ADMIN_REPORT_TYPES_CARDS = REPORT_TYPES_CARDS.filter(
    (card) =>
    card.value === REPORT_TYPES.ALL_ILT_TRAININGS ||
    card.value === REPORT_TYPES.INVOICE_STATUS
);

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ReportAllTrainings: React.FC<ReportAllTrainingsProps> = ({
    onChange: onChangeReportCriteria,
    roleType,
}): JSX.Element => {
    const { record: globalState } = useGlobalState();
    const [payload, dispatch] = useReportReducer(reducer, initialState, {
        initializer: initializer(roleType, globalState.currentIdentity?.user?.organization),
        validate,
    });

    const {
        instructors,
        organizations,
        reportType,
        invoiceStatusType,
        dateRangeStart,
        dateRangeEnd,
    } = payload;

    const onReportTypeChange = (reportType: string) => dispatch({ type: "reportType", reportType });

    const onDateRangeChange = (date: Array<Date | null> = []) => {
        const [start, end] = date;
        dispatch({ type: "dateRange", dateRangeStart: start, dateRangeEnd: end });
    };

    useEffect(() => {
        onChangeReportCriteria({
            ...payload,
            organizationIds: payload.organizations.map((organization) => organization.id),
            instructorIds: payload.instructors.map((instructor) => instructor.id),
        });
    }, [onChangeReportCriteria, payload]);

    return (
        <>
            <div className="content-wrap">
                <div className="content">
                    <SkipNavContent>
                        <div className={CSS_CLASS_NAME}>
                            <ReportSelection
                                title={t("allTrainingsReport")}
                                roleType={roleType}
                                adminOnly={
                                    <div className="report-section">
                                        <Paragraph size={ParagraphSize.Large}>
                                            {t("iWantToRunAnAllTrainingReportBasedOn")}
                                        </Paragraph>
                                        <FormRadioCardList
                                            id="reportType"
                                            formFieldName="reportType"
                                            label={t("reportType")}
                                            labelScreenReaderOnly={true}
                                            iconSize={IconSizes.Base}
                                            onChange={onReportTypeChange}
                                            radioCards={
                                                REPORT_TYPES_CARDS
                                            }
                                            required={true}
                                            style={RadioCardStyle.Horizontal}
                                            value={reportType}
                                        />
                                    </div>
                                }
                            />

                            {roleType == RoleType.AenAdministrator && (
                                <div className="report-section">
                                    <Paragraph size={ParagraphSize.Large}>
                                        {t("iWantToRunAnAllTrainingReportBasedOn")}
                                    </Paragraph>
                                    <FormRadioCardList
                                        id="reportType"
                                        formFieldName="reportType"
                                        label={t("reportType")}
                                        labelScreenReaderOnly={true}
                                        iconSize={IconSizes.Base}
                                        onChange={onReportTypeChange}
                                        radioCards={AEN_ADMIN_REPORT_TYPES_CARDS}
                                        required={true}
                                        style={RadioCardStyle.Horizontal}
                                        value={reportType}
                                    />
                                </div>
                            )}

                            {reportType === REPORT_TYPES.EN_INSTRUCTOR && (
                                <ReportInstructorSelection
                                    instructors={instructors}
                                    isRequired={true}
                                    onInstructorsChanged={(instructors) =>
                                        dispatch({ type: "updateInstructors", instructors })
                                    }
                                />
                            )}

                            {reportType === REPORT_TYPES.ORGANIZATION &&
                                roleType === RoleType.ClientAdmin &&
                                globalState.currentIdentity?.user?.organization !== undefined && (
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                <th className="organization-name">
                                                    {t("organizationName")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {organizations.map((organization) => (
                                                <tr key={organization.id}>
                                                    <td className="organization-name">
                                                        {organization.name}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </DataTable>
                                )}

                            {reportType === REPORT_TYPES.ORGANIZATION &&
                                roleType !== RoleType.ClientAdmin && (
                                    <ReportOrganizationSelection
                                        organizations={organizations}
                                        isRequired={true}
                                        onOrganizationsChanged={(organizations) =>
                                            dispatch({ type: "updateOrganizations", organizations })
                                        }
                                    />
                                )}

                            {reportType === REPORT_TYPES.INVOICE_STATUS && (
                                <ReportInvoiceStatusSelection
                                    isRequired={true}
                                    invoiceStatusType={invoiceStatusType}
                                    onInvoiceStatusTypeChanged={(invoiceStatusType: string) =>
                                        dispatch({ type: "invoiceStatusType", invoiceStatusType })
                                    }
                                />
                            )}

                            {StringUtils.hasValue(reportType) && (
                                <div className="report-section half">
                                    <Paragraph size={ParagraphSize.Large}>
                                        {t("inThisTimePeriod")}
                                    </Paragraph>
                                    <FormCalendarDateRangePicker
                                        required={reportType === REPORT_TYPES.ALL_TRAININGS}
                                        endDate={dateRangeEnd}
                                        formFieldName="dateRange"
                                        handleEventDateRangeChange={onDateRangeChange}
                                        label={t("dateRange")}
                                        startDate={dateRangeStart}
                                    />
                                </div>
                            )}
                        </div>
                    </SkipNavContent>
                </div>
            </div>
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ReportAllTrainings };

// #endregion Exports
